import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { getTransactions, getReportSummary } from '../utils/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Download, Share2, Calendar, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { formatCurrency } from '../utils/formatCurrency';

const COLORS = ['#064E3B', '#9A3412', '#1E40AF', '#7C2D12', '#4C1D95', '#0F766E', '#BE123C', '#065F46'];

export const Reports = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async (start = null, end = null) => {
    setLoading(true);
    try {
      const [summaryData, transactionsData] = await Promise.all([
        getReportSummary(start, end),
        getTransactions(start && end ? { start_date: start, end_date: end } : {})
      ]);
      
      setSummary(summaryData);
      setTransactions(transactionsData);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    if (startDate && endDate) {
      const start = new Date(startDate).toISOString();
      const end = new Date(endDate).toISOString();
      fetchReport(start, end);
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    fetchReport();
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(20);
    doc.text('Financial Report', 14, 20);
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);
    if (startDate && endDate) {
      doc.text(`Period: ${startDate} to ${endDate}`, 14, 36);
    }
    
    doc.setFontSize(14);
    doc.text('Summary', 14, 50);
    
    const summaryData = [
      ['Total Cash In', formatCurrency(summary?.total_cash_in || 0)],
      ['Total Cash Out', formatCurrency(summary?.total_cash_out || 0)],
      ['Total Charges', formatCurrency(summary?.total_charges || 0)],
      ['Net Cashflow', formatCurrency(summary?.net_cashflow || 0)]
    ];
    
    doc.autoTable({
      startY: 55,
      head: [['Metric', 'Amount']],
      body: summaryData,
      theme: 'grid'
    });
    
    if (summary?.category_breakdown?.length > 0) {
      doc.text('Category Breakdown', 14, doc.lastAutoTable.finalY + 15);
      
      const categoryData = summary.category_breakdown.map(cat => [
        cat.category,
        formatCurrency(cat.amount),
        cat.count.toString()
      ]);
      
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Category', 'Amount', 'Transactions']],
        body: categoryData,
        theme: 'grid'
      });
    }
    
    doc.save('financial-report.pdf');
    toast.success('Report exported successfully');
  };

  const shareReport = async () => {
    const text = `Financial Report\n\nTotal Cash In: ${formatCurrency(summary?.total_cash_in || 0)}\nTotal Cash Out: ${formatCurrency(summary?.total_cash_out || 0)}\nNet Cashflow: ${formatCurrency(summary?.net_cashflow || 0)}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Financial Report',
          text: text
        });
        toast.success('Report shared successfully');
      } catch (error) {
        if (error.name !== 'AbortError') {
          toast.error('Failed to share report');
        }
      }
    } else {
      navigator.clipboard.writeText(text);
      toast.success('Report copied to clipboard');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-stone-600">Loading report...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between gap-4"
        >
          <div>
            <h1 className="text-4xl font-bold text-stone-900 tracking-tight mb-2">Reports</h1>
            <p className="text-stone-600">Analyze your financial activity</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={shareReport}
              variant="outline"
              className="rounded-full"
              data-testid="share-report-button"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
            <Button
              onClick={exportToPDF}
              className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-full"
              data-testid="export-pdf-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </motion.div>

        {/* Date Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-stone-200 rounded-xl p-6"
        >
          <div className="flex items-center mb-4">
            <Calendar className="w-5 h-5 text-emerald-900 mr-2" />
            <h2 className="text-lg font-semibold text-stone-900">Date Range Filter</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                data-testid="start-date-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-stone-50 mt-1"
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                data-testid="end-date-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-stone-50 mt-1"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleFilter}
                className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-full"
                data-testid="apply-filter-button"
              >
                Apply Filter
              </Button>
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleClearFilter}
                variant="outline"
                className="w-full rounded-full"
                data-testid="clear-filter-button"
              >
                Clear Filter
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-stone-200 rounded-xl p-6"
            data-testid="summary-cash-in"
          >
            <p className="text-sm font-medium text-stone-600 mb-2">Total Cash In</p>
            <p className="text-3xl font-bold text-emerald-900">{formatCurrency(summary?.total_cash_in || 0)}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white border border-stone-200 rounded-xl p-6"
            data-testid="summary-cash-out"
          >
            <p className="text-sm font-medium text-stone-600 mb-2">Total Cash Out</p>
            <p className="text-3xl font-bold text-orange-800">{formatCurrency(summary?.total_cash_out || 0)}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white border border-stone-200 rounded-xl p-6"
            data-testid="summary-charges"
          >
            <p className="text-sm font-medium text-stone-600 mb-2">Total Charges</p>
            <p className="text-3xl font-bold text-stone-900">{formatCurrency(summary?.total_charges || 0)}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white border border-stone-200 rounded-xl p-6"
            data-testid="summary-net-cashflow"
          >
            <p className="text-sm font-medium text-stone-600 mb-2">Net Cashflow</p>
            <p className={`text-3xl font-bold ${
              (summary?.net_cashflow || 0) >= 0 ? 'text-emerald-900' : 'text-orange-800'
            }`}>
              {formatCurrency(summary?.net_cashflow || 0)}
            </p>
          </motion.div>
        </div>

        {/* Category Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white border border-stone-200 rounded-xl p-6"
        >
          <h2 className="text-xl font-semibold text-stone-900 mb-6">Category Breakdown</h2>
          {summary?.category_breakdown?.length > 0 ? (
            <div className="space-y-4">
              {[...summary.category_breakdown].sort((a, b) => b.amount - a.amount).map((cat, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-4 bg-stone-50 rounded-lg hover:bg-stone-100 cursor-pointer transition-colors" 
                  data-testid={`category-breakdown-${index}`}
                  onClick={() => navigate(`/transactions?category=${encodeURIComponent(cat.category)}`)}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-sm flex-shrink-0" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <div>
                      <p className="font-semibold text-stone-900">{cat.category}</p>
                      <p className="text-sm text-stone-600">{cat.count} transaction{cat.count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-bold text-orange-800">{formatCurrency(cat.amount)}</p>
                    <ChevronRight className="w-5 h-5 text-stone-400" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center text-stone-500">
              No expense data available
            </div>
          )}
        </motion.div>
      </div>
    </DashboardLayout>
  );
};